import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/Button";
import { Calendar, Info } from "lucide-react";
import EditDayScheduleModal from "../../components/EditDayScheduleModal";
import BookingScheduleTable from "../../components/ui/BookingScheduleTable";
import ToggleSetting from "../../components/ui/ToggleSetting";
import SectionHeader from "../../components/ui/SectionHeader";
import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import {
  updateBookingTimes,
  BookingTime,
  getBookingTime,
  updateBookingTime,
  UpdateBookingTimePayload,
} from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { PageHeader } from "../../components/ui/PageHeader";
import InfoContainer from "../../components/ui/InfoContainer";
import { useAuth } from "../../auth/AuthContext";

interface DaySchedule {
  day: string;
  openTime: string;
  closeTime: string;
  allowBooking: boolean;
  originalData?: BookingTime;
  locationId?: number;
}

interface LocationSchedule {
  locationId: number;
  locationName: string;
  timezone?: string;
  schedule: DaySchedule[];
}

const BookingsSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } =
    useOutletContext<SettingsOutletContextType>();
  const { syncSettingsToLocalStorage } = useAuth();
  const [allowEmployeeSelection, setAllowEmployeeSelection] = useState(true);
  const [payWithXeConnect, setPayWithXeConnect] = useState(true);
  const [showPricingOnOnlineBooking, setShowPricingOnOnlineBooking] = useState(true);
  const [saving, setSaving] = useState(false);

  const [locationSchedules, setLocationSchedules] = useState<LocationSchedule[]>([]);

  useEffect(() => {
    if (settingsData && settingsData.settings) {
      setAllowEmployeeSelection(
        settingsData.settings.employee_selection === "active"
      );
      setPayWithXeConnect(
        settingsData.settings.pay_with_xeconnect === "active"
      );
      const pricingFlag = settingsData.settings.show_pricing_on_online_booking;
      setShowPricingOnOnlineBooking(
        pricingFlag !== false &&
          pricingFlag !== 0 &&
          pricingFlag !== "0" &&
          pricingFlag !== "false" &&
          pricingFlag !== "inactive" &&
          pricingFlag !== "disabled" &&
          pricingFlag !== "deactive"
      );

      // Use service_locations to group by location
      if (settingsData.service_locations && Object.keys(settingsData.service_locations).length > 0) {
        const daysMap: Record<string, number> = {
          monday: 0,
          tuesday: 1,
          wednesday: 2,
          thursday: 3,
          friday: 4,
          saturday: 5,
          sunday: 6,
        };

        const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

        // Group booking times by location_id
        const locationMap = new Map<number, LocationSchedule>();

        // First, process service_locations to get location info
        Object.values(settingsData.service_locations).forEach((item: any) => {
          const locationId = item.location_id;
          if (!locationMap.has(locationId)) {
            locationMap.set(locationId, {
              locationId: locationId,
              locationName: item.locations?.name || `Location ${locationId}`,
              timezone: item.locations?.timezone?.zone_name || item.locations?.timezone?.name,
              schedule: new Array(7).fill(null).map((_, i) => ({
                day: dayNames[i],
                openTime: "09:00",
                closeTime: "18:00",
                allowBooking: false,
                locationId: locationId,
              })),
            });
          }
        });

        // Then, populate schedules from booking_times
        if (settingsData.booking_times && settingsData.booking_times.length > 0) {
          settingsData.booking_times.forEach((time) => {
            const locationSchedule = locationMap.get(time.location_id);
            if (locationSchedule) {
              const dayIndex = daysMap[time.day.toLowerCase()];
              if (dayIndex !== undefined) {
                locationSchedule.schedule[dayIndex] = {
                  day: time.day.charAt(0).toUpperCase() + time.day.slice(1),
                  openTime: time.start_time,
                  closeTime: time.end_time,
                  allowBooking: time.status === "enabled",
                  originalData: time,
                  locationId: time.location_id,
                };
              }
            }
          });
        }

        setLocationSchedules(Array.from(locationMap.values()));
      } else if (settingsData.booking_times && settingsData.booking_times.length > 0) {
        // Fallback: if service_locations is not available, use booking_times directly
        const daysMap: Record<string, number> = {
          monday: 0,
          tuesday: 1,
          wednesday: 2,
          thursday: 3,
          friday: 4,
          saturday: 5,
          sunday: 6,
        };

        const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        const sortedSchedule: DaySchedule[] = new Array(7).fill(null);

        settingsData.booking_times.forEach((time) => {
          const dayIndex = daysMap[time.day.toLowerCase()];
          if (dayIndex !== undefined && !sortedSchedule[dayIndex]) {
            sortedSchedule[dayIndex] = {
              day: time.day.charAt(0).toUpperCase() + time.day.slice(1),
              openTime: time.start_time,
              closeTime: time.end_time,
              allowBooking: time.status === "enabled",
              originalData: time,
            };
          }
        });

        for (let i = 0; i < 7; i++) {
          if (!sortedSchedule[i]) {
            sortedSchedule[i] = {
              day: dayNames[i],
              openTime: "09:00",
              closeTime: "18:00",
              allowBooking: false,
            };
          }
        }

        // Create a default location schedule
        setLocationSchedules([{
          locationId: settingsData.booking_times[0]?.location_id || 0,
          locationName: "Default Location",
          schedule: sortedSchedule,
        }]);
      }
    }
  }, [settingsData]);

  const handlePayWithXeConnectChange = async (checked: boolean) => {
    if (!settingsData?.settings) return;

    setPayWithXeConnect(checked);
    setSaving(true);

    try {
      await updateBookingTimes({
        booking_time_type: settingsData.settings.booking_time_type || "sum",
        booking_per_day: Number(settingsData.settings.booking_per_day) || 0,
        disable_slot: settingsData.settings.disable_slot === "enabled" ? 1 : 0,
        employee_selection: settingsData.settings.employee_selection || "inactive",
        multi_task_user: Number(settingsData.settings.multi_task_user) || 0,
        duration: settingsData.settings.duration || 30,
        duration_type: settingsData.settings.duration_type || "minutes",
        pay_with_xeconnect: checked ? "active" : "inactive",
        show_pricing_on_online_booking: showPricingOnOnlineBooking ? "active" : "inactive",
      });

      toast.success("Pay with XE Connect setting updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
      console.log("Pay with XE Connect setting updated successfully");
    } catch (error: any) {
      console.error("Failed to update Pay with XE Connect setting", error);
      toast.error("Failed to update Pay with XE Connect setting. Please try again.");
      // Revert the toggle on error
      setPayWithXeConnect(!checked);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!settingsData?.settings) return;
    setSaving(true);
    try {
      await updateBookingTimes({
        booking_time_type: settingsData.settings.booking_time_type || "sum",
        booking_per_day: Number(settingsData.settings.booking_per_day) || 0,
        disable_slot: settingsData.settings.disable_slot === "enabled" ? 1 : 0,
        employee_selection: settingsData.settings.employee_selection || "inactive",
        multi_task_user: Number(settingsData.settings.multi_task_user) || 0,
        duration: settingsData.settings.duration || 30,
        duration_type: settingsData.settings.duration_type || "minutes",
        pay_with_xeconnect: payWithXeConnect ? "active" : "inactive",
        show_pricing_on_online_booking: showPricingOnOnlineBooking ? "active" : "inactive",
      });

      toast.success("Booking settings updated successfully!");
      refreshSettings();
      await syncSettingsToLocalStorage();
      console.log("Booking settings updated successfully");
    } catch (error: any) {
      console.error("Failed to save booking settings", error);
      toast.error("Failed to save booking settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleBooking = async (locationIndex: number, dayIndex: number) => {
    const locationSchedule = locationSchedules[locationIndex];
    const day = locationSchedule?.schedule[dayIndex];
    if (!day) return;

    // If we have a booking time ID, update via API
    if (day.originalData?.id) {
      setSaving(true);
      try {
        const newStatus = !day.allowBooking ? "enabled" : "disabled";

        const payload: UpdateBookingTimePayload = { status: newStatus };

        const response = await updateBookingTime(day.originalData.id, payload);

        if (response.success) {
          const updated = response.data;
          const newLocationSchedules = [...locationSchedules];
          newLocationSchedules[locationIndex].schedule[dayIndex] = {
            ...newLocationSchedules[locationIndex].schedule[dayIndex],
            allowBooking: newStatus === "enabled",
            openTime: updated.start_time || day.openTime,
            closeTime: updated.end_time || day.closeTime,
            originalData: updated,
          };
          setLocationSchedules(newLocationSchedules);

          toast.success(`Booking ${newStatus === "enabled" ? "enabled" : "disabled"} successfully!`);
          refreshSettings();
          await syncSettingsToLocalStorage();
        }
      } catch (error: any) {
        console.error("Failed to update booking status:", error);
        toast.error("Failed to update booking status. Please try again.");
        // Revert the toggle on error
        const newLocationSchedules = [...locationSchedules];
        newLocationSchedules[locationIndex].schedule[dayIndex].allowBooking = day.allowBooking;
        setLocationSchedules(newLocationSchedules);
      } finally {
        setSaving(false);
      }
    } else {
      // No existing booking time, just update local state
      const newLocationSchedules = [...locationSchedules];
      newLocationSchedules[locationIndex].schedule[dayIndex].allowBooking =
        !newLocationSchedules[locationIndex].schedule[dayIndex].allowBooking;
      setLocationSchedules(newLocationSchedules);
      toast.info("Changes saved locally. Please create booking times first.");
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocationIndex, setEditingLocationIndex] = useState<number | null>(null);
  const [editingDayIndex, setEditingDayIndex] = useState<number | null>(null);
  const [modalData, setModalData] = useState({
    openTime: "",
    closeTime: "",
    slotDuration: "30",
    allowMultipleBookings: "Yes",
    maxBookingsAllowed: "0",
    maxBookingsPerDay: "0",
    maxBookingsPerSlot: "0",
    status: "Active",
  });

  const handleEditDay = async (locationIndex: number, dayIndex: number) => {
    const locationSchedule = locationSchedules[locationIndex];
    if (!locationSchedule) return;

    const day = locationSchedule.schedule[dayIndex];
    if (!day) return;

    setEditingLocationIndex(locationIndex);
    setEditingDayIndex(dayIndex);

    // If we have an original booking time ID, fetch latest data from API
    if (day.originalData?.id) {
      try {
        const response = await getBookingTime(day.originalData.id);
        if (response.success && response.data.booking_time) {
          const bookingTime = response.data.booking_time;
          setModalData({
            openTime: bookingTime.start_time,
            closeTime: bookingTime.end_time,
            slotDuration: bookingTime.slot_duration?.toString() || "30",
            allowMultipleBookings: bookingTime.multiple_booking === "yes" ? "Yes" : "No",
            maxBookingsAllowed: bookingTime.max_booking?.toString() || "0",
            maxBookingsPerDay: bookingTime.per_day_max_booking?.toString() || "0",
            maxBookingsPerSlot: bookingTime.per_slot_max_booking?.toString() || "0",
            status: bookingTime.status === "enabled" ? "Active" : "Inactive",
          });
        } else {
          // Fallback to local data
          const original = day.originalData;
          setModalData({
            openTime: day.openTime,
            closeTime: day.closeTime,
            slotDuration: original?.slot_duration?.toString() || "30",
            allowMultipleBookings: original?.multiple_booking === "yes" ? "Yes" : "No",
            maxBookingsAllowed: original?.max_booking?.toString() || "0",
            maxBookingsPerDay: original?.per_day_max_booking?.toString() || "0",
            maxBookingsPerSlot: original?.per_slot_max_booking?.toString() || "0",
            status: day.allowBooking ? "Active" : "Inactive",
          });
        }
      } catch (error) {
        console.error("Failed to fetch booking time details:", error);
        // Fallback to local data
        const original = day.originalData;
        setModalData({
          openTime: day.openTime,
          closeTime: day.closeTime,
          slotDuration: original?.slot_duration?.toString() || "30",
          allowMultipleBookings: original?.multiple_booking === "yes" ? "Yes" : "No",
          maxBookingsAllowed: original?.max_booking?.toString() || "0",
          maxBookingsPerDay: original?.per_day_max_booking?.toString() || "0",
          maxBookingsPerSlot: original?.per_slot_max_booking?.toString() || "0",
          status: day.allowBooking ? "Active" : "Inactive",
        });
      }
    } else {
      // No existing booking time, use local data
      const original = day.originalData;
      setModalData({
        openTime: day.openTime,
        closeTime: day.closeTime,
        slotDuration: original?.slot_duration?.toString() || "30",
        allowMultipleBookings: original?.multiple_booking === "yes" ? "Yes" : "No",
        maxBookingsAllowed: original?.max_booking?.toString() || "0",
        maxBookingsPerDay: original?.per_day_max_booking?.toString() || "0",
        maxBookingsPerSlot: original?.per_slot_max_booking?.toString() || "0",
        status: day.allowBooking ? "Active" : "Inactive",
      });
    }

    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLocationIndex(null);
    setEditingDayIndex(null);
  };

  // Helper function to format time according to company's time_format setting
  const formatTimeForAPI = (time: string): string => {
    if (!time || !time.trim()) {
      console.warn("formatTimeForAPI: Empty time provided");
      return time;
    }

    // Get the company's time format from settings (default to "h:i A" if not available)
    const timeFormat = settingsData?.settings?.time_format || "h:i A";

    console.log("formatTimeForAPI input:", { time, timeFormat });

    // Clean the input
    let cleaned = time.trim().replace(/\s+/g, " ");

    // For formats with AM/PM (h:i A, h:i a)
    if (timeFormat.includes("A") || timeFormat.includes("a")) {
      // Check if time is in 24-hour format (e.g., "21:00", "09:00")
      const is24HourFormat = /^\d{1,2}:\d{2}$/.test(cleaned) && !cleaned.match(/(AM|PM)/i);

      if (is24HourFormat) {
        // Convert 24-hour to 12-hour format
        const [hours, minutes] = cleaned.split(":");
        let hour24 = parseInt(hours, 10);
        let hour12 = hour24;
        let period = "AM";

        if (hour24 === 0) {
          hour12 = 12;
          period = "AM";
        } else if (hour24 === 12) {
          hour12 = 12;
          period = "PM";
        } else if (hour24 > 12) {
          hour12 = hour24 - 12;
          period = "PM";
        } else {
          period = "AM";
        }

        // Format according to timeFormat: "h:i A" means hour:minute AM/PM
        // h can be 1-12 (not zero-padded), but we'll use 2 digits for consistency
        cleaned = `${String(hour12).padStart(2, "0")}:${minutes} ${period}`;
      } else {
        // Time already has AM/PM, just normalize it
        // Extract time and period
        const timeMatch = cleaned.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let hour12 = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          let period = timeMatch[3].toUpperCase();

          // Normalize hour (should be 1-12)
          if (hour12 === 0) hour12 = 12;
          if (hour12 > 12) {
            // If hour > 12, it's likely a mistake, convert to PM
            hour12 = hour12 - 12;
            period = "PM";
          }

          // Format: ensure 2-digit hour and proper spacing
          cleaned = `${String(hour12).padStart(2, "0")}:${minutes} ${period}`;
        } else {
          // Try to extract just the time part and infer AM/PM
          const timeOnlyMatch = cleaned.match(/(\d{1,2}):(\d{2})/);
          if (timeOnlyMatch) {
            let hour12 = parseInt(timeOnlyMatch[1], 10);
            const minutes = timeOnlyMatch[2];

            // If hour > 12, assume PM
            let period = hour12 >= 12 ? "PM" : "AM";
            if (hour12 > 12) {
              hour12 = hour12 - 12;
            } else if (hour12 === 0) {
              hour12 = 12;
            }

            cleaned = `${String(hour12).padStart(2, "0")}:${minutes} ${period}`;
          }
        }
      }

      // Match the case of the format (A = uppercase, a = lowercase)
      if (timeFormat.includes("A")) {
        cleaned = cleaned.replace(/(AM|PM)/i, (match) => match.toUpperCase());
      } else if (timeFormat.includes("a")) {
        cleaned = cleaned.replace(/(AM|PM)/i, (match) => match.toLowerCase());
      }

      // Final validation: must match pattern HH:MM AM/PM with exactly one space
      if (!cleaned.match(/^\d{1,2}:\d{2}\s+(AM|PM)$/)) {
        console.error("formatTimeForAPI: Failed to format time correctly", { original: time, formatted: cleaned });
        // Last resort: try to parse and rebuild
        const lastMatch = cleaned.match(/(\d{1,2}):(\d{2})/);
        if (lastMatch) {
          let h = parseInt(lastMatch[1], 10);
          const m = lastMatch[2];
          const p = h >= 12 ? "PM" : "AM";
          if (h > 12) h = h - 12;
          if (h === 0) h = 12;
          cleaned = `${String(h).padStart(2, "0")}:${m} ${timeFormat.includes("A") ? p.toUpperCase() : p.toLowerCase()}`;
        }
      }
    } else if (timeFormat === "H:i") {
      // 24-hour format
      if (cleaned.match(/(AM|PM)/i)) {
        // Convert from 12-hour to 24-hour
        const timeMatch = cleaned.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let hour12 = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const period = timeMatch[3].toUpperCase();
          let hour24 = hour12;

          if (period === "PM" && hour12 !== 12) {
            hour24 = hour12 + 12;
          } else if (period === "AM" && hour12 === 12) {
            hour24 = 0;
          }

          cleaned = `${String(hour24).padStart(2, "0")}:${minutes}`;
        }
      }
      // Ensure HH:MM format
      if (!cleaned.match(/^\d{2}:\d{2}$/)) {
        const [h, m] = cleaned.split(":");
        cleaned = `${String(parseInt(h || "0", 10)).padStart(2, "0")}:${m || "00"}`;
      }
    }

    console.log("formatTimeForAPI output:", cleaned);
    return cleaned;
  };

  const handleModalSubmit = async () => {
    if (editingLocationIndex === null || editingDayIndex === null) {
      handleModalClose();
      return;
    }

    const locationSchedule = locationSchedules[editingLocationIndex];
    const day = locationSchedule?.schedule[editingDayIndex];
    if (!day) {
      handleModalClose();
      return;
    }

    // If we have a booking time ID, update via API
    if (day.originalData?.id) {
      setSaving(true);
      try {
        // Validate times before formatting
        if (!modalData.openTime || !modalData.closeTime) {
          toast.error("Start time and end time are required.");
          setSaving(false);
          return;
        }

        // Format times to ensure space before AM/PM
        const formattedStartTime = formatTimeForAPI(modalData.openTime);
        const formattedEndTime = formatTimeForAPI(modalData.closeTime);

        // Validate formatted times
        if (!formattedStartTime || !formattedEndTime) {
          console.error("Time formatting failed:", {
            originalStart: modalData.openTime,
            originalEnd: modalData.closeTime,
            formattedStart: formattedStartTime,
            formattedEnd: formattedEndTime,
            timeFormat: settingsData?.settings?.time_format,
          });
          toast.error("Failed to format times correctly. Please check the time format.");
          setSaving(false);
          return;
        }

        // Log for debugging
        console.log("Sending times to API:", {
          originalStart: modalData.openTime,
          formattedStart: formattedStartTime,
          originalEnd: modalData.closeTime,
          formattedEnd: formattedEndTime,
          timeFormat: settingsData?.settings?.time_format,
        });

        // Build payload according to backend validation rules
        const isEnabled = modalData.status === "Active";
        const multipleBooking = modalData.allowMultipleBookings === "Yes" ? "yes" : "no";

        const payload: UpdateBookingTimePayload = {
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          multiple_booking: multipleBooking,
          // max_booking is required_if multiple_booking is "yes"
          max_booking: multipleBooking === "yes" ? Number(modalData.maxBookingsAllowed) || 0 : undefined,
          // per_day_max_booking is required (always send, min 0)
          per_day_max_booking: Number(modalData.maxBookingsPerDay) || 0,
          // slot_duration is required_if status is enabled, min 1 (not 0 or 30 default)
          slot_duration: isEnabled ? Math.max(1, Number(modalData.slotDuration) || 1) : undefined,
          status: isEnabled ? "enabled" : "disabled",
        };

        const response = await updateBookingTime(day.originalData.id, payload);

        if (response.success) {
          // Update local state with response data
          const newLocationSchedules = [...locationSchedules];
          const isEnabled = modalData.status === "Active";
          newLocationSchedules[editingLocationIndex].schedule[editingDayIndex] = {
            ...newLocationSchedules[editingLocationIndex].schedule[editingDayIndex],
            openTime: modalData.openTime,
            closeTime: modalData.closeTime,
            allowBooking: isEnabled,
            originalData: response.data, // Update with latest data from server
          };
          setLocationSchedules(newLocationSchedules);

          toast.success("Booking time updated successfully!");
          refreshSettings();
          await syncSettingsToLocalStorage();
        }
      } catch (error: any) {
        console.error("Failed to update booking time:", error);
        toast.error("Failed to update booking time. Please try again.");
      } finally {
        setSaving(false);
      }
    } else {
      // No existing booking time, just update local state
      const newLocationSchedules = [...locationSchedules];
      const isEnabled = modalData.status === "Active";
      newLocationSchedules[editingLocationIndex].schedule[editingDayIndex] = {
        ...newLocationSchedules[editingLocationIndex].schedule[editingDayIndex],
        openTime: modalData.openTime,
        closeTime: modalData.closeTime,
        allowBooking: isEnabled,
      };
      setLocationSchedules(newLocationSchedules);
      toast.info("Changes saved locally. Please create booking times first.");
    }

    handleModalClose();
  };

  const handleModalInputChange = (field: string, value: string) => {
    setModalData((prev) => ({ ...prev, [field]: value }));
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading booking settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        title="Booking Settings"
        description="Configure booking preferences and schedule"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Calendar size={18} className="text-white" />
            Booking Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            {/* Booking Preferences Section */}
            <div>
              <SectionHeader title="Booking Preferences" />

              {/* Info Box */}
              <InfoContainer
                message="Configure how customers can book appointments and make payments"
                iconSize={14}
                className="mb-4"
              />

              <div className="space-y-4">
                {/* Pay with XE Connect */}
                <ToggleSetting
                  label="Pay with XE Connect"
                  description="Enable XE Connect payment option for bookings"
                  checked={payWithXeConnect}
                  onChange={handlePayWithXeConnectChange}
                />

                <ToggleSetting
                  label="Show pricing on online booking and website"
                  description="Show service prices to customers on the online booking page and website"
                  checked={showPricingOnOnlineBooking}
                  onChange={setShowPricingOnOnlineBooking}
                />
              </div>
            </div>

            {/* Booking Schedule Section - Grouped by Location */}
            <div>
              <SectionHeader title="Weekly Schedule by Location" />

              {/* Info Box */}
              <InfoContainer
                message="Set your business hours for each day of the week by location. Click the edit icon to modify times."
                iconSize={14}
                className="mb-4"
              />

              {locationSchedules.length > 0 ? (
                <div className="space-y-6">
                  {locationSchedules.map((locationSchedule, locationIndex) => (
                    <div key={locationSchedule.locationId} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Location Header */}
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {locationSchedule.locationName}
                        </h4>
                        {locationSchedule.timezone && (
                          <p className="text-xs text-gray-500 mt-1">
                            Timezone: {locationSchedule.timezone}
                          </p>
                        )}
                      </div>

                      {/* Schedule Table for this Location */}
                      <BookingScheduleTable
                        schedule={locationSchedule.schedule}
                        onToggleBooking={(dayIndex) => toggleBooking(locationIndex, dayIndex)}
                        onEditDay={(dayIndex) => handleEditDay(locationIndex, dayIndex)}
                        saving={saving}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No booking schedules found. Please configure your locations first.
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Day Modal */}
      <EditDayScheduleModal
        isOpen={isModalOpen && editingLocationIndex !== null && editingDayIndex !== null}
        dayName={
          editingLocationIndex !== null && editingDayIndex !== null
            ? locationSchedules[editingLocationIndex]?.schedule[editingDayIndex]?.day || ""
            : ""
        }
        modalData={modalData}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        onInputChange={handleModalInputChange}
      />
    </div>
  );
};

export default BookingsSettings;
